package com.qrkontaq.ui.components.forms

import androidx.compose.runtime.*
import com.qrkontaq.ui.theme.AppColors
import com.qrkontaq.ui.theme.toRgbString
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Span

@Composable
fun TextInput(
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    label: String? = null,
    placeholder: String = "",
    error: String? = null,
    enabled: Boolean = true,
    type: InputType<String> = InputType.Text
) {
    val colorMode = ColorMode.current
    val colors = AppColors.getColors(colorMode)

    // gap(5) keeps the label hugging its own input; the bottom margin separates
    // this field from the next one, so labels group with the field below them.
    Column(modifier = modifier.fillMaxWidth().gap(5.px).margin(bottom = 14.px)) {
        label?.let {
            SpanText(
                text = it,
                modifier = Modifier
                    .fontSize(13.px)
                    .fontWeight(com.varabyte.kobweb.compose.css.FontWeight.Medium)
                    .color(colors.textSecondary)
            )
        }

        Input(type = type) {
            value(value)
            onInput { onValueChange(it.value) }
            if (placeholder.isNotEmpty()) attr("placeholder", placeholder)
            if (!enabled) attr("disabled", "")
            (label ?: placeholder).takeIf { it.isNotEmpty() }?.let { attr("aria-label", it) }
            if (error != null) attr("aria-invalid", "true")

            style {
                width(100.percent)
                padding(12.px)
                property("border-radius", "8px")
                fontSize(16.px)
                property("outline", "none")
                property("box-sizing", "border-box")
                property("transition", "border-color 200ms ease")
                property("font-family", "inherit")
                property("background-color", colors.surface.toRgbString())
                property("color", colors.textPrimary.toRgbString())

                if (error != null) {
                    property("border", "1px solid ${AppColors.Error.toRgbString()}")
                } else {
                    property("border", "1px solid ${colors.border.toRgbString()}")
                }

                if (!enabled) {
                    opacity(0.6)
                    property("cursor", "not-allowed")
                }
            }
        }

        error?.let {
            SpanText(
                text = it,
                modifier = Modifier
                    .fontSize(12.px)
                    .color(AppColors.Error)
                    .margin(top = 4.px)
            )
        }
    }
}

// Lucide eye / eye-off, matching the icon style used across the admin panel.
private const val EYE_SVG =
    """<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"/><circle cx="12" cy="12" r="3"/></svg>"""
private const val EYE_OFF_SVG =
    """<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49"/><path d="M14.084 14.158a3 3 0 0 1-4.242-4.242"/><path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143"/><path d="m2 2 20 20"/></svg>"""

@Composable
fun PasswordInput(
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    label: String? = null,
    placeholder: String = "",
    error: String? = null,
    enabled: Boolean = true
) {
    val colorMode = ColorMode.current
    val colors = AppColors.getColors(colorMode)
    var visible by remember { mutableStateOf(false) }

    Column(modifier = modifier.fillMaxWidth().gap(5.px).margin(bottom = 14.px)) {
        label?.let {
            SpanText(
                text = it,
                modifier = Modifier
                    .fontSize(13.px)
                    .fontWeight(com.varabyte.kobweb.compose.css.FontWeight.Medium)
                    .color(colors.textSecondary)
            )
        }

        Div(attrs = {
            style {
                property("position", "relative")
                width(100.percent)
            }
        }) {
            Input(type = if (visible) InputType.Text else InputType.Password) {
                value(value)
                onInput { onValueChange(it.value) }
                if (placeholder.isNotEmpty()) attr("placeholder", placeholder)
                if (!enabled) attr("disabled", "")
                (label ?: placeholder).takeIf { it.isNotEmpty() }?.let { attr("aria-label", it) }
                if (error != null) attr("aria-invalid", "true")

                style {
                    width(100.percent)
                    padding(12.px)
                    // Room for the eye button so long passwords don't run under it.
                    property("padding-right", "44px")
                    property("border-radius", "8px")
                    fontSize(16.px)
                    property("outline", "none")
                    property("box-sizing", "border-box")
                    property("transition", "border-color 200ms ease")
                    property("font-family", "inherit")
                    property("background-color", colors.surface.toRgbString())
                    property("color", colors.textPrimary.toRgbString())

                    if (error != null) {
                        property("border", "1px solid ${AppColors.Error.toRgbString()}")
                    } else {
                        property("border", "1px solid ${colors.border.toRgbString()}")
                    }

                    if (!enabled) {
                        opacity(0.6)
                        property("cursor", "not-allowed")
                    }
                }
            }

            Button(attrs = {
                attr("type", "button")
                // Keep Tab moving straight from password to the submit button.
                attr("tabindex", "-1")
                attr("aria-label", if (visible) "Hide password" else "Show password")
                if (!enabled) attr("disabled", "")
                onClick { visible = !visible }
                style {
                    property("position", "absolute")
                    property("right", "6px")
                    property("top", "50%")
                    property("transform", "translateY(-50%)")
                    property("display", "flex")
                    property("align-items", "center")
                    property("justify-content", "center")
                    property("background", "none")
                    property("border", "none")
                    padding(6.px)
                    property("cursor", if (enabled) "pointer" else "not-allowed")
                    property("color", colors.textSecondary.toRgbString())
                }
            }) {
                // key() forces the span to remount so the ref re-runs with the new icon.
                key(visible) {
                    Span(attrs = {
                        style {
                            property("display", "flex")
                            property("width", "20px")
                            property("height", "20px")
                        }
                        ref { el ->
                            el.innerHTML = if (visible) EYE_OFF_SVG else EYE_SVG
                            onDispose { el.innerHTML = "" }
                        }
                    })
                }
            }
        }

        error?.let {
            SpanText(
                text = it,
                modifier = Modifier
                    .fontSize(12.px)
                    .color(AppColors.Error)
                    .margin(top = 4.px)
            )
        }
    }
}

@Composable
fun TextArea(
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    label: String? = null,
    placeholder: String = "",
    error: String? = null,
    enabled: Boolean = true,
    rows: Int = 4
) {
    val colorMode = ColorMode.current
    val colors = AppColors.getColors(colorMode)

    Column(modifier = modifier.fillMaxWidth().gap(5.px).margin(bottom = 14.px)) {
        label?.let {
            SpanText(
                text = it,
                modifier = Modifier
                    .fontSize(13.px)
                    .fontWeight(com.varabyte.kobweb.compose.css.FontWeight.Medium)
                    .color(colors.textSecondary)
            )
        }

        org.jetbrains.compose.web.dom.TextArea {
            value(value)
            onInput { onValueChange(it.value) }
            attr("rows", rows.toString())
            if (placeholder.isNotEmpty()) attr("placeholder", placeholder)
            if (!enabled) attr("disabled", "")
            (label ?: placeholder).takeIf { it.isNotEmpty() }?.let { attr("aria-label", it) }
            if (error != null) attr("aria-invalid", "true")

            style {
                width(100.percent)
                padding(12.px)
                property("border-radius", "8px")
                fontSize(16.px)
                property("outline", "none")
                property("box-sizing", "border-box")
                property("resize", "vertical")
                property("transition", "border-color 200ms ease")
                property("font-family", "inherit")
                property("background-color", colors.surface.toRgbString())
                property("color", colors.textPrimary.toRgbString())

                if (error != null) {
                    property("border", "1px solid ${AppColors.Error.toRgbString()}")
                } else {
                    property("border", "1px solid ${colors.border.toRgbString()}")
                }
            }
        }

        error?.let {
            SpanText(
                text = it,
                modifier = Modifier
                    .fontSize(12.px)
                    .color(AppColors.Error)
                    .margin(top = 4.px)
            )
        }
    }
}
